<template>
  <div>
    <div v-for="(section, index) in sections" :key="section.title">
      <div class="test-gray-dark font-bold mb-4">{{ section.title }}</div>
      <div class="grid grid-cols-1 gap-x-8 gap-y-4">
        <div v-for="field in section.fields"
             :key="field.key"
             class="w-full lg:w-2/3 xl:w-1/3"
        >

          <base-label :id="field.key"
                      :label="field.label"
                      :tooltip="field.tooltip"
                      label-classes="font-normal mb-1"
          />

          <component :name="field.key"
                     :is="field.component"
                     :value="field.value"
                     :max-length="25"
                     :lang-config="value.langConfig"
                     :theme-config="value.themeConfig"
                     @input="field.method($event)"
          />
        </div>
      </div>

      <div v-if="index < sections.length - 1" class="border-0 border-solid border-t border-soft my-6"/>
    </div>
  </div>
</template>

<script>
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'
import BaseFileInput from '@/components/base-components/BaseFileInput.vue'
import SettingsBotThemeLauncherIcon from '@/components/settings/bot-theme/SettingsBotThemeLauncherIcon.vue'
import omit from 'ramda/src/omit'

const UPLOAD_TOOLTIP = 'Add a PNG, JPG, or GIF that is under 150KB. Images with transparent backgrounds works best.'

export default {
  name: 'SettingsBotThemeForm',

  components: { BaseLabel },

  props: {
    value: {
      type: Object,
      default: () => {}
    },
    imageCache: {
      type: Object,
      required: true
    }
  },

  emits: ['input', 'update:image-cache'],

  data() {
    return {
      values: {
        widgetIcon: '',
        logoUrl: null
      }
    }
  },

  computed: {
    themeConfig: {
      get() {
        if (this.value.themeConfig) {
          return this.value.themeConfig
        }

        return {}
      },
      set(themeUpdate) {
        this.$emit('input', {
          themeConfig: { ...this.value.themeConfig, ...themeUpdate }
        })
      }
    },

    langConfig: {
      get() {
        if (this.value.langConfig) {
          return this.value.langConfig
        }

        return {}
      },
      set(langUpdate) {
        this.$emit('input', {
          langConfig: { ...this.value.langConfig, ...langUpdate }
        })
      }
    },

    themeHeaderLogoUrl() {
      return this.themeConfig.header && this.themeConfig.header.logoUrl
    },

    themeLauncherImageUrl() {
      return this.themeConfig.launcher && this.themeConfig.launcher.imageUrl
    },

    headerLogo: {
      get() {
        return this.imageCache.headerLogo || this.themeHeaderLogoUrl || null
      },
      set(headerLogo) {
        if (!headerLogo && this.themeHeaderLogoUrl) {
          this.themeConfig = {
            header: omit(['logoUrl'], this.value.themeConfig.header)
          }
          return
        }

        this.$emit('update:image-cache', {
          ...this.imageCache, headerLogo
        })
      }
    },

    buttonIcon: {
      get() {
        return this.imageCache.buttonIcon || this.themeLauncherImageUrl || null
      },
      set(buttonIcon) {
        if (!buttonIcon && this.themeLauncherImageUrl) {
          this.themeConfig = {
            launcher: omit(['imageUrl'], this.value.themeConfig.launcher)
          }
          return
        }

        this.$emit('update:image-cache', {
          ...this.imageCache, buttonIcon
        })
      }
    },

    sections() {
      return [
        {
          title: 'Chat header',
          fields: [
            {
              label: 'Text',
              key: 'welcome',
              method: headerText => this.setHeaderText(headerText),
              value: this.langConfig.default?.['welcome'] || '',
              component: ValidatedInput
            },
            {
              label: 'Company logo',
              key: 'headerLogo',
              value: this.headerLogo,
              tooltip: UPLOAD_TOOLTIP,
              method: headerLogo => this.setHeaderLogo(headerLogo),
              component: BaseFileInput
            }
          ]
        },
        {
          title: 'Launcher',
          fields: [
            {
              label: 'Text',
              key: 'buttonTitle',
              method: buttonTitle => this.setButtonTitle(buttonTitle),
              value: this.langConfig.default?.['buttonTitle'] || '',
              component: ValidatedInput
            },
            {
              label: 'Widget icon',
              key: 'buttonIcon',
              value: this.buttonIcon,
              tooltip: UPLOAD_TOOLTIP,
              method: buttonIcon => this.setButtonIcon(buttonIcon),
              component: SettingsBotThemeLauncherIcon
            }
          ]
        }
      ]
    }
  },

  methods: {
    setHeaderText(headerText) {
      if (!this.value.themeConfig) return
      this.langConfig = {
        default: {
          ...this.langConfig.default,
          welcome: headerText
        }
      }
      this.themeConfig = {
        header: {
          ...this.value.themeConfig.header,
          logoOnly: !headerText
        }
      }
    },

    setButtonTitle(buttonTitle) {
      if (!this.value.themeConfig) return
      this.langConfig = {
        default: {
          ...this.langConfig.default,
          buttonTitle
        }
      }
    },

    setHeaderLogo(file) {
      this.headerLogo = file
    },

    setButtonIcon(file) {
      this.buttonIcon = file
    }
  }
}
</script>
